import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/animations.css";
import {
  Heart,
  Plus,
  Paperclip,
  Globe,
  ArrowUp,
  ChevronDown,
  ExternalLink,
  MessageCircle,
  Star,
  Users,
  Eye,
  Play,
  Loader,
  FileText,
  Upload,
  Search,
  Database,
  BarChart3,
  Send,
  X,
  Download,
  Share2,
  Mic,
  MicOff,
} from "lucide-react";
import { ExperienceAPI, ChatRequest, Project } from "../services/experienceAPI";
import FileUploadModal from "../components/FileUploadModal";
import {
  DataAnalysisService,
  AnalysisResult,
} from "../services/dataAnalysisService";
import ChartRenderer from "../components/ChartRenderer";
import DragDropTextarea from "../components/DragDropTextarea";
import AnalysisStepRenderer from "../components/AnalysisStepRenderer";
import AnalysisProgressBorder from "../components/AnalysisProgressBorder";
import { rawColors } from "../design-system/tokens/colors";

const InteractContainer = styled.div`
  height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  color: ${rawColors.text.white};
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
        circle at 20% 80%,
        rgba(255, 230, 242, 0.3) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(230, 242, 255, 0.3) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 40% 40%,
        rgba(255, 230, 242, 0.2) 0%,
        transparent 50%
      );
    pointer-events: none;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${rawColors.text.white};
`;

const LogoImage = styled.img`
  height: 50px;
  // width: 40px;
`;

const Nav = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;
`;

const NavLink = styled.a`
  color: ${rawColors.text.white};
  text-decoration: none;
  opacity: 0.8;
  transition: opacity 0.2s;
  font-size: 0.9rem;

  &:hover {
    opacity: 1;
  }
`;

const Button = styled.button<{ variant?: "primary" | "secondary" | "ghost" }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: ${rawColors.structural.white};
          color: #0f0f23;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(255,255,255,0.2);
          }
        `;
      case "secondary":
        return `
          background: rgba(255,255,255,0.1);
          color: ${rawColors.text.white};
          border: 1px solid rgba(255,255,255,0.2);
          &:hover {
            background: rgba(255,255,255,0.15);
          }
        `;
      case "ghost":
        return `
          background: transparent;
          color: ${rawColors.text.white};
          &:hover {
            background: rgba(255,255,255,0.1);
          }
        `;
      default:
        return `
          background: ${rawColors.structural.white};
          color: #0f0f23;
        `;
    }
  }}
`;

const MainContent = styled.div`
  display: flex;
  height: calc(100vh - 80px);
  max-width: 1464px;
  margin: 0 auto;
  position: relative;
  z-index: 10;

  /* Responsive design for smaller screens */
  @media (max-width: 1200px) {
    max-width: 100%;
    padding: 0 1rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    min-height: calc(100vh - 80px);
  }
`;

const LeftPanel = styled.div`
  width: 400px;
  height: 100%;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.02);

  /* Responsive design for smaller screens */
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.01);
  overflow: hidden;
  height: 100%;
  min-width: 0; /* Allow flex item to shrink below content size */
`;

const ChatHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
`;

const ChatTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0;
`;

const ChatSubtitle = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0.5rem 0 0 0;
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Message = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  gap: 0.5rem;
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  background: ${(props) =>
    props.isUser
      ? "linear-gradient(135deg, #ff6b9d 0%, #8b5cf6 100%)"
      : "rgba(255, 255, 255, 0.1)"};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1rem;
  max-width: 80%;
  word-wrap: break-word;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const MessageTime = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ChatInputContainer = styled.div`
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  position: relative;
`;

const ChatInputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.75rem;
`;

const VoiceButton = styled.button<{ isRecording: boolean }>`
  background: ${(props) =>
    props.isRecording ? "rgba(255, 255, 255)" : "rgba(255, 255, 255, 0.1)"};
  border: 1px solid
    ${(props) =>
      props.isRecording ? "rgba(255, 255, 255)" : "rgba(255, 255, 255, 0.2)"};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  color: ${rawColors.text.white};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 26px;
  right: 26px;

  // &:hover {
  //   background: ${(props) =>
    props.isRecording
      ? "linear-gradient(135deg, #ff3742 0%, #ff2f3a 100%)"
      : "rgba(255, 255, 255, 0.2)"};
  //   transform: translateY(-1px);
  // }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  ${(props) =>
    props.isRecording &&
    `
    animation: pulse 1.5s ease-in-out infinite;
    
    @keyframes pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(255, 255, 255);
      }
      70% {
        box-shadow: 0 0 0 10px rgba(255, 71, 87, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(255, 71, 87, 0);
      }
    }
  `}
`;

const ChatTextarea = styled.textarea`
  flex: 1;
  background: transparent;
  border: none;
  color: ${rawColors.text.white};
  font-size: 0.9rem;
  font-family: inherit;
  resize: none;
  min-height: 24px;
  max-height: 100px;
  outline: none;
  line-height: 1.4;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #ff6b9d 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  color: ${rawColors.text.white};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 157, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ChatActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  color: ${rawColors.text.white};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const DataHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DataTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0;
`;

const DataActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DataContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  height: 100%;
  min-width: 0;
  max-width: 100%;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* Responsive design for smaller screens */
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const DataCard = styled.div`
  // background: rgba(255, 255, 255, 0.05);
  // border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  // padding: 1.5rem;
  transition: all 0.2s;
  overflow: hidden;
  min-width: 0;
  max-width: 100%;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  /* Responsive design for smaller screens */
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const DataCardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DataCardContent = styled.div`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  overflow: hidden; /* Prevent content overflow */
  word-wrap: break-word; /* Allow long words to wrap */
`;

const ChartPlaceholder = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 100%;

  /* Add subtle scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 600px; /* Ensure minimum width for readability */
  border-collapse: collapse;
  font-size: 0.9rem;
  white-space: nowrap; /* Prevent text wrapping */
`;

const Th = styled.th`
  background: rgba(255, 255, 255, 0.1);
  color: ${rawColors.text.white};
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap; /* Prevent header text wrapping */
  min-width: 120px; /* Ensure minimum column width */
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap; /* Prevent cell text wrapping */
  overflow: hidden;
  text-overflow: ellipsis; /* Show ellipsis for long content */
  max-width: 200px; /* Limit cell width */
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.6);
`;

const SpinningLoader = styled.div`
  animation: spin 1s linear infinite;
  height: 18px;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const InteractPage: React.FC = () => {
  const [messages, setMessages] = useState<
    Array<{
      id: string;
      text: string;
      isUser: boolean;
      timestamp: Date;
    }>
  >([
    {
      id: "1",
      text: "Hello! I'm your AI assistant. How can I help you analyze your data today?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dataContent, setDataContent] = useState<
    Array<{
      id: string;
      type: "summary" | "chart" | "table" | "file";
      title: string;
      content: any;
    }>
  >([]);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceSupported, setIsVoiceSupported] = useState(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(
    null
  );
  const [showCharts, setShowCharts] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentAnalysisStep, setCurrentAnalysisStep] = useState<string>("");
  const [analysisStepData, setAnalysisStepData] = useState<any>(null);
  const [analysisProgress, setAnalysisProgress] = useState<number>(0);
  const [analysisSteps, setAnalysisSteps] = useState<
    Array<{
      step: string;
      status: string;
      data?: any;
      timestamp: Date;
    }>
  >([]);
  const [showAnalysisProgressBorder, setShowAnalysisProgressBorder] =
    useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dataContentEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    dataContentEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const triggerAnalysis = async () => {
    // console.log("🚀 Triggering analysis...");
    const uploadedFileId = sessionStorage.getItem("uploadedFileId");
    // console.log("📁 Uploaded file ID:", uploadedFileId);
    if (!uploadedFileId) return;

    setAnalysisSteps([]);
    setAnalysisProgress(0);
    setShowAnalysisProgressBorder(true);

    const analysisStartMessage = {
      id: Date.now().toString(),
      text: "🚀 Starting AI analysis... This may take a few minutes.",
      isUser: false,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, analysisStartMessage]);

    try {
      // console.log("🔧 Calling DataAnalysisService.analyzeFile...");
      const analysisResult = await DataAnalysisService.analyzeFile(
        uploadedFileId,
        "interact-page",
        (progress) => {
          // console.log("📊 Analysis progress:", progress);
          setAnalysisProgress(progress.progress);
        },
        (step, status, data) => {
          // console.log("🔄 Step callback:", { step, status, data });
          handleAnalysisStepUpdate(step, status, data);
        }
      );

      setAnalysisResult(analysisResult);
      setShowCharts(true);
      // setShowAnalysisProgressBorder(false);

      const analysisCompleteMessage = {
        id: (Date.now() + 1).toString(),
        text: `🎉 Analysis complete! Found ${analysisResult.insights.length} insights and created ${analysisResult.dashboards?.length} dashboards and recommended ${analysisResult.nextSteps?.length} next steps.`,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, analysisCompleteMessage]);
    } catch (error) {
      console.error("Analysis failed:", error);
      setShowAnalysisProgressBorder(false);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: `❌ Analysis failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleAnalysisStepUpdate = (
    step: string,
    status: string,
    data?: any
  ) => {
    // console.log("🔍 Analysis Step Update:", { step, status, data });
    setCurrentAnalysisStep(step);
    setAnalysisStepData(data);

    // Update analysis steps state for real-time rendering
    setAnalysisSteps((prev) => {
      const existingIndex = prev.findIndex((s) => s.step === step);
      const newStep = {
        step,
        status,
        data,
        timestamp: new Date(),
      };

      if (existingIndex !== -1) {
        // Update existing step
        const updated = [...prev];
        updated[existingIndex] = newStep;
        // console.log("📝 Updated analysis steps:", updated);
        return updated;
      } else {
        // Add new step
        const newSteps = [...prev, newStep];
        // console.log("📝 Added new analysis step:", newSteps);
        return newSteps;
      }
    });

    let messageText = "";
    let messageId = `${step}-${status}-${Date.now()}`;

    switch (step) {
      case "preview":
        if (status === "processing") {
          messageText = "📊 Creating data preview...";
        } else if (status === "completed") {
          messageText = "✅ Data preview created successfully";
        } else if (status === "failed") {
          messageText = "❌ Failed to create data preview";
        }
        break;

      case "insights":
        if (status === "processing") {
          messageText = "🧠 Generating AI insights...";
        } else if (status === "completed") {
          const insightCount = data?.insights?.length || 0;
          messageText = `✅ Generated ${insightCount} AI insights`;
        } else if (status === "failed") {
          messageText = "❌ Failed to generate AI insights";
        }
        break;

      case "dashboards":
        if (status === "processing") {
          messageText = "📈 Creating dashboards...";
        } else if (status === "completed") {
          const dashboardCount = data?.dashboards?.length || 0;
          const totalCharts =
            data?.dashboards?.reduce(
              (total: number, dashboard: any) =>
                total + (dashboard.charts?.length || 0),
              0
            ) || 0;
          messageText = `✅ Created ${dashboardCount} dashboards with ${totalCharts} charts`;
        } else if (status === "failed") {
          messageText = "❌ Failed to create dashboards";
        }
        break;

      case "next-steps":
        if (status === "processing") {
          messageText = "🎯 Generating next steps...";
        } else if (status === "completed") {
          const stepCount = data?.next_steps?.length || 0;
          messageText = `✅ Generated ${stepCount} next step recommendations`;
        } else if (status === "failed") {
          messageText = "❌ Failed to generate next steps";
        }
        break;

      case "executing-sql":
        if (status === "processing") {
          messageText = "⚡ Executing SQL queries for visualizations...";
        } else if (status === "completed") {
          messageText = "✅ SQL queries executed successfully";
        } else if (status === "failed") {
          messageText = "❌ Failed to execute SQL queries";
        }
        break;

      case "complete":
        // if (status === "completed") {
        //   messageText = "🎉 All analysis steps completed successfully!";
        // }
        break;

      case "error":
        if (status === "failed") {
          messageText = `❌ Analysis error: ${data?.error || "Unknown error"}`;
        }
        break;

      default:
        if (status === "processing") {
          messageText = `⏳ Processing ${step}...`;
        } else if (status === "completed") {
          messageText = `✅ ${step} completed`;
        } else if (status === "failed") {
          messageText = `❌ ${step} failed`;
        }
    }

    if (messageText) {
      const stepMessage = {
        id: messageId,
        text: messageText,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const existingIndex = prev.findIndex((msg) =>
          msg.id.startsWith(`${step}-`)
        );
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = stepMessage;
          return updated;
        } else {
          return [...prev, stepMessage];
        }
      });
    }
  };

  useEffect(() => {
    const experienceChatInput = sessionStorage.getItem(
      "experiencePageChatInput"
    );
    const experienceFileInput = sessionStorage.getItem(
      "experiencePageFileInput"
    );
    const uploadedFileName = sessionStorage.getItem("uploadedFileName");

    if (experienceChatInput) {
      const userMessage = {
        id: Date.now().toString(),
        text: experienceChatInput,
        isUser: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsSubmitting(true);
      sessionStorage.removeItem("experiencePageChatInput");
    }

    if (experienceFileInput) {
      setIsSubmitting(false);
      const uploadPromptMessage = JSON.parse(experienceFileInput);
      uploadPromptMessage.timestamp = new Date();
      setMessages((prev) => [...prev, uploadPromptMessage]);
      sessionStorage.removeItem("experiencePageFileInput");

      setAnalysisSteps([]);
      setAnalysisProgress(0);
      triggerAnalysis();
    }

    setIsSubmitting(false);

    setTimeout(() => {
      setIsPageLoaded(true);
    }, 100);

    setIsVoiceSupported(
      typeof navigator !== "undefined" &&
        !!navigator.mediaDevices &&
        !!navigator.mediaDevices.getUserMedia
    );
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((inputValue.trim() || selectedFile) && !isSubmitting && !isUploading) {
      const userMessage = {
        id: Date.now().toString(),
        text:
          inputValue +
          (selectedFile ? `\n\nSelected file: ${selectedFile.name}` : ""),
        isUser: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setIsSubmitting(true);
      setShowAnalysisProgressBorder(false);

      try {
        if (selectedFile) {
          await handleFileUploadAndAnalysis(selectedFile);
        } else {
          // TODO: Call chat API
          await new Promise((resolve) => setTimeout(resolve, 2000));

          const aiResponse = {
            id: (Date.now() + 1).toString(),
            text: `I've analyzed your request: "${inputValue}". Here are the insights and visualizations you requested.`,
            isUser: false,
            timestamp: new Date(),
          };

          setMessages((prev) => [...prev, aiResponse]);

          const newDataContent = {
            id: Date.now().toString(),
            type: "summary" as const,
            title: "AI Analysis Summary",
            content: `Based on your query about "${inputValue}", I've identified several key insights and patterns in your data. The analysis shows significant trends that could impact your business decisions.`,
          };

          setDataContent((prev) => [...prev, newDataContent]);
        }
      } catch (error) {
        console.error("Chat submission failed:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        await processAudioToText(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Unable to access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudioToText = async (audioBlob: Blob) => {
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob);

      // Dummy for now
      setTimeout(() => {
        const mockTranscription =
          "Analyze my sales data and show me the trends";
        setInputValue(mockTranscription);
        textareaRef.current?.focus();
      }, 1000);

      // const response = await fetch('/api/speech-to-text', {
      //   method: 'POST',
      //   body: formData
      // });
      // const { transcription } = await response.json();
      // setInputValue(transcription);
    } catch (error) {
      console.error("Error processing audio:", error);
      alert("Error processing voice input. Please try again.");
    }
  };

  const toggleRecording = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUploadAndAnalysis = async (file: File) => {
    setIsUploading(true);
    try {
      const uploadResponse = await ExperienceAPI.uploadFile(file);
      // console.log("File upload response:", uploadResponse);

      if (uploadResponse.success) {
        const uploadMessage = {
          id: (Date.now() + 1).toString(),
          text: `✅ File uploaded successfully: ${file.name} (${uploadResponse.metadata.row_count} rows, ${uploadResponse.metadata.column_count} columns)`,
          isUser: false,
          timestamp: new Date(),
        };
        sessionStorage.setItem("uploadedFileId", uploadResponse.file_id);
        sessionStorage.setItem("uploadedFileName", file.name);
        setMessages((prev) => [...prev, uploadMessage]);
        setTimeout(() => {
          triggerAnalysis();
        }, 100);
      } else {
        throw new Error(uploadResponse.message || "File upload failed");
      }
    } catch (error) {
      console.error("File upload failed:", error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: `❌ File upload failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setIsSubmitting(false);
    }
  };

  return (
    <InteractContainer>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isPageLoaded ? 1 : 0 }}
        transition={{ duration: 0.6 }}
      >
        <Header>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{
              opacity: isPageLoaded ? 1 : 0,
              y: isPageLoaded ? 0 : -20,
            }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Logo>
              <LogoImage src="/logo-with-text-white.png" alt="MagicBI Logo" />
            </Logo>
          </motion.div>
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{
              opacity: isPageLoaded ? 1 : 0,
              y: isPageLoaded ? 0 : -20,
            }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Nav>
              <NavLink href="#help">Help</NavLink>
              <NavLink href="#settings">Settings</NavLink>
              <Button variant="ghost">Export</Button>
              <Button variant="primary">
                <Share2 size={14} />
                Share
              </Button>
            </Nav>
          </motion.nav>
        </Header>

        <MainContent>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{
              opacity: isPageLoaded ? 1 : 0,
              x: isPageLoaded ? 0 : -50,
            }}
            transition={{ duration: 0.6, delay: 0.3 }}
            style={{ width: "400px" }}
          >
            <LeftPanel>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: isPageLoaded ? 1 : 0,
                  y: isPageLoaded ? 0 : 20,
                }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <ChatHeader>
                  <ChatTitle>AI Chat Assistant</ChatTitle>
                  <ChatSubtitle>
                    Ask questions, upload files, and get insights
                  </ChatSubtitle>
                </ChatHeader>
              </motion.div>

              <ChatMessages>
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    // transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  >
                    <Message isUser={message.isUser}>
                      <MessageBubble isUser={message.isUser}>
                        {message.text}
                      </MessageBubble>
                      <MessageTime>{formatTime(message.timestamp)}</MessageTime>
                    </Message>
                  </motion.div>
                ))}
                {isSubmitting && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Message isUser={false}>
                      <MessageBubble isUser={false}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <SpinningLoader>
                            <Loader size={18} />
                          </SpinningLoader>
                          Analyzing your request...
                        </div>
                      </MessageBubble>
                    </Message>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </ChatMessages>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: isPageLoaded ? 1 : 0,
                  y: isPageLoaded ? 0 : 20,
                }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <ChatInputContainer>
                  <AnalysisProgressBorder
                    progress={analysisProgress}
                    isVisible={showAnalysisProgressBorder}
                    currentStep={currentAnalysisStep}
                  />
                  <form onSubmit={handleSubmit}>
                    <ChatInputWrapper>
                      <DragDropTextarea
                        value={inputValue}
                        onChange={setInputValue}
                        placeholder="Ask me anything about your data..."
                        onFileSelect={handleFileSelect}
                        onFileRemove={handleFileRemove}
                        selectedFile={selectedFile}
                        disabled={isSubmitting || isUploading}
                        fileInputRef={fileInputRef}
                        onKeyPress={handleKeyPress}
                      />
                      {isVoiceSupported && (
                        <VoiceButton
                          isRecording={isRecording}
                          onClick={toggleRecording}
                          disabled={isSubmitting || isUploading}
                          title={
                            isRecording ? "Stop recording" : "Start voice input"
                          }
                        >
                          {isRecording ? (
                            <Mic size={16} color="#00000080" />
                          ) : (
                            <Mic size={16} />
                          )}
                        </VoiceButton>
                      )}
                      <SendButton
                        type="submit"
                        disabled={
                          isSubmitting ||
                          isUploading ||
                          (!inputValue.trim() && !selectedFile)
                        }
                      >
                        {isSubmitting || isUploading ? (
                          <SpinningLoader>
                            <Loader size={18} />
                          </SpinningLoader>
                        ) : (
                          <Send size={16} />
                        )}
                      </SendButton>
                    </ChatInputWrapper>
                  </form>

                  <ChatActions>
                    <ActionButton onClick={handleFileUploadClick}>
                      <Upload size={14} />
                      Upload File
                    </ActionButton>
                    <ActionButton>
                      <Search size={14} />
                      Web Search
                    </ActionButton>
                  </ChatActions>
                </ChatInputContainer>
              </motion.div>
            </LeftPanel>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{
              opacity: isPageLoaded ? 1 : 0,
              x: isPageLoaded ? 0 : 50,
            }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ flex: 1 }}
          >
            <RightPanel>
              <DataContent>
                {(() => {
                  // console.log("🎯 Rendering analysis steps:", analysisSteps);
                  return null;
                })()}

                {/* Debug display */}
                {/* <div style={{ 
                  background: 'rgba(255, 0, 0, 0.1)', 
                  padding: '10px', 
                  margin: '10px', 
                  border: '1px solid red',
                  color: rawColors.text.white
                }}>
                  <strong>Debug Info:</strong><br/>
                  analysisSteps.length: {analysisSteps.length}<br/>
                  analysisProgress: {analysisProgress}%<br/>
                  currentAnalysisStep: {currentAnalysisStep}<br/>
                  dataContent.length: {dataContent.length}
                </div> */}

                {analysisSteps.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <DataCard>
                      <DataCardTitle>
                        <BarChart3 size={20} />
                        Analysis Progress: {analysisProgress}%
                      </DataCardTitle>
                      <DataCardContent>
                        <div style={{ marginBottom: "1rem" }}>
                          <div
                            style={{
                              width: "100%",
                              height: "8px",
                              background: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${analysisProgress}%`,
                                height: "100%",
                                background:
                                  "linear-gradient(90deg, #ff6b9d, #8b5cf6)",
                                transition: "width 0.3s ease",
                              }}
                            />
                          </div>
                        </div>

                        {analysisSteps.map((stepData, index) => (
                          <motion.div
                            key={`${
                              stepData.step
                            }-${stepData.timestamp.getTime()}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <AnalysisStepRenderer
                              step={stepData.step}
                              status={stepData.status}
                              data={stepData.data}
                            />
                          </motion.div>
                        ))}
                      </DataCardContent>
                    </DataCard>
                  </motion.div>
                )}

                {dataContent.length === 0 && analysisSteps.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{
                      opacity: isPageLoaded ? 1 : 0,
                      y: isPageLoaded ? 0 : 30,
                    }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                  >
                    <DataCard>
                      <DataCardTitle>
                        <BarChart3 size={20} />
                        Welcome to Your Data Workspace
                      </DataCardTitle>
                      <DataCardContent>
                        Start a conversation with the AI assistant to generate
                        insights, charts, and analysis from your data. You can
                        upload files, connect to databases, or ask questions
                        about your data.
                      </DataCardContent>
                    </DataCard>
                  </motion.div>
                ) : null}
                <div ref={dataContentEndRef} />
              </DataContent>
            </RightPanel>
          </motion.div>
        </MainContent>
      </motion.div>
    </InteractContainer>
  );
};

export default InteractPage;
